import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useForm, FormContext } from 'react-hook-form'
import { renderFormByType } from './subforms/renderFormByType'
import { PopTypeSelector } from './subforms/PopTypeSelector'
import { PopFormValues, PopType } from '../types'
import { Flex, useToast } from '@chakra-ui/core'
import { PopSwitch } from './fileds/PopSwitch'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { AppDispatch } from 'config/redux'
import { createPopAdminV, setIsLoading } from 'redux/actions/pops'
import { PROFILE_IMAGE_PLACEHOLDER } from './utils'

export interface PopFormShellRef {
  submit: () => void
}

interface PopFormShellProps {
  onClose: () => void
  onCreated?: (data: PopFormValues) => void
  validateOnly?: boolean
  initialPopData?: PopFormValues | null
}

export const PopFormShell = forwardRef<PopFormShellRef, PopFormShellProps>(
  ({ onClose, onCreated, validateOnly, initialPopData }, ref) => {
    const toast = useToast()
    const methodsRef = useRef<any>(null)
    const dispatch: AppDispatch = useDispatch()
    const { rid } = useSelector((state: RootState) => state.config.config)

    const [selectedType, setSelectedType] = useState<PopType>(initialPopData?.pop_type || PopType.Default)

    const methods = useForm<PopFormValues>({
      defaultValues: initialPopData || {},
      mode: 'onChange',
    })

    methodsRef.current = methods

    const onSubmit = async (data: PopFormValues) => {
      dispatch(setIsLoading(true))
      try {
        const body: any = validateOnly ? { ...data, is_scheduled: true } : data

        if (!body.data.video) {
          body.data.video = null
        }

        if (!body.data.pop_image_url) {
          body.data.pop_image_url = null
        }
        if (!body.data.profile_image_url) {
          body.data.profile_image_url = PROFILE_IMAGE_PLACEHOLDER
        }

        const res = await dispatch(createPopAdminV({ body }))
        if (res.success) {
          toast({
            title: 'Pop created',
            description: 'Pop created successfully',
            status: 'success',
            position: 'top-right',
          })

          onCreated?.({ ...body, pop_id: res.data })
          onClose()
        }
      } catch (e) {
        console.log(e)
        toast({
          title: 'Pop creation failed',
          description: 'Pop creation failed',
          status: 'error',
          position: 'top-right',
        })
      } finally {
        dispatch(setIsLoading(false))
      }
    }
    const isLongRangePop = methods.getValues('is_long_range_pop') as boolean

    useImperativeHandle(ref, () => ({
      submit: () => {
        methods.handleSubmit(onSubmit)()
      },
      onClose,
    }))

    useEffect(() => {
      if (rid) {
        methods.setValue('rid', rid)
      }
    }, [rid, methods])

    useEffect(() => {
      methods.setValue('pop_type', selectedType)
      methods.clearError()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedType])

    return (
      <FormContext<PopFormValues> {...methods}>
        <Flex w="100%" alignItems="center" justifyContent="space-between" style={{ columnGap: '10px' }}>
          <PopTypeSelector value={selectedType} onChange={setSelectedType} />

          <Flex flexDir="column" alignItems="flex-end" style={{ rowGap: '10px' }}>
            <PopSwitch name="is_long_range_pop" title="enable_long_range" defaultValue={isLongRangePop} />

            {selectedType === PopType.RePop && <PopSwitch name="data.is_pop_post" title="create_pop_as_post" />}

            {(selectedType === PopType.AiPop || selectedType === PopType.AiRePop) && (
              <Flex flexDir="column" alignItems="flex-end" style={{ rowGap: '10px' }}>
                <PopSwitch name="data.is_inj_reports" title="enable_injury_reports" />
                <PopSwitch name="data.is_voice_mode" title="enable_voice_mode" />
              </Flex>
            )}
          </Flex>
        </Flex>
        {renderFormByType(selectedType)}
      </FormContext>
    )
  }
)
